import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Clock3, Copy, Inbox, LayoutDashboard, LogOut, Mail, MailCheck, MessageSquare, Package, Package2, Paperclip, Quote, RefreshCw, Reply, Send, Settings, ShieldCheck, Sparkles, Star, UserPlus, Users, X, Zap } from "lucide-react";
import { toast } from "sonner";
import {
  changeAdminPassword,
  deleteAdminClient,
  createAdminClient,
  createAdminProduct,
  createAdminTestimonial,
  fetchAdminHomeHeroContent,
  deleteAdminInquiry,
  deleteAdminProduct,
  fetchAdminClients,
  fetchAdminInquiries,
  fetchAdminInboxEmails,
  fetchAdminOutboxEmails,
  fetchAdminSentEmails,
  fetchAdminDashboard,
  fetchAdminMe,
  fetchAdminTestimonials,
  fetchCategories,
  fetchProducts,
  loginAdmin,
  logoutAdmin,
  deleteAdminOutboxDraft,
  deleteAdminTestimonial,
  saveAdminOutboxDraft,
  sendAdminMailboxEmail,
  sendAdminInquiryEmail,
  updateAdminInquiry,
  updateAdminHomeHeroContent,
  updateAdminProduct,
} from "../lib/api";
import { fileToBase64, filesToBase64, isValidImageFile } from "../lib/fileHelpers";

const initialProductForm = {
  slug: "",
  name: "",
  tagline: "",
  description: "",
  category_slug: "",
  industries: "",
  images: [],
  detail_images: [],
  specifications: "[{\"key\":\"Capacity\",\"value\":\"Custom\"}]",
  advantages: "",
  applications: "",
  featured: false,
  price_from: "",
};

const initialClientForm = {
  name: "",
  company: "",
  industry: "",
  website: "",
  logo: "",
  active: true,
};

const initialTestimonialForm = {
  name: "",
  designation: "",
  quote: "",
  rating: 5,
};

const initialLoginForm = {
  username: "",
  password: "",
};

const initialEmailComposer = {
  inquiryId: "",
  to: "",
  cc: "",
  subject: "",
  body: "",
  templateKey: "executive_acknowledgement",
  includeDetails: true,
  mode: "inquiry",
  sourceEmail: null,
  attachments: [],
};

const initialHomeHeroForm = {
  latest_delivery_overline: "Latest delivery",
  latest_delivery_title: "Robotic MIG welding cell shipped to a Tier-1 auto major",
  cycle_time: "42 sec",
  uptime: "99.3%",
  weld_quality: "Cat A",
  payback: "14 months",
  machines_delivered: 480,
  enterprise_clients: 120,
  engineering_years: 18,
};

const TOAST_DURATION = 4000;
const INQUIRIES_POLL_MS = 4000;
const MAILBOX_POLL_MS = 5000;

function formatInquiryStatus(status) {
  const value = String(status || "pending").replace(/_/g, " ").trim();
  return value.length ? value[0].toUpperCase() + value.slice(1) : "Pending";
}

function buildInquiryDetailsBlock(inquiry) {
  const details = [
    ["Inquiry ID", inquiry?.id],
    ["Status", formatInquiryStatus(inquiry?.status)],
    ["Product", inquiry?.product_name || "General enquiry"],
    ["Quantity", inquiry?.quantity],
    ["Budget", inquiry?.budget],
    ["Expected delivery", inquiry?.expected_delivery],
    ["Location", inquiry?.location],
    ["Phone", inquiry?.phone],
    ["Company", inquiry?.company],
  ]
    .filter(([, value]) => Boolean(value))
    .map(([label, value]) => `- ${label}: ${value}`)
    .join("\n");

  if (!details) {
    return "Inquiry details:\n- No additional details were provided.";
  }

  return `Inquiry details:\n${details}`;
}

function buildEmailTemplate(templateKey, inquiry, includeDetails = true) {
  const customerName = inquiry?.name || "there";
  const productName = inquiry?.product_name || "your requested machinery";
  const detailsBlock = includeDetails ? `\n\n${buildInquiryDetailsBlock(inquiry)}` : "";

  const templates = {
    acknowledgement: `Hello ${customerName},\n\nThank you for contacting Buildwit Technology. We have received your inquiry for ${productName} and our sales team is reviewing the requirement.${detailsBlock}\n\nWe will share the next update shortly.`,
    quotation: `Hello ${customerName},\n\nThank you for your interest in ${productName}. To prepare an accurate quotation, please confirm your expected quantity, preferred delivery timeline, and installation location.${detailsBlock}\n\nOnce we receive these details, we will send a tailored commercial offer.`,
    follow_up: `Hello ${customerName},\n\nFollowing up on your inquiry for ${productName}. We are ready to support you with technical specifications, pricing, and delivery planning based on your requirement.${detailsBlock}\n\nPlease let us know a convenient time for a quick call.`,
    closure: `Hello ${customerName},\n\nThank you for your inquiry regarding ${productName}. We are marking this thread as closed for now.${detailsBlock}\n\nIf you need assistance again, simply reply to this email and we will reopen the request immediately.`,
  };

  const selectedTemplate = templates[templateKey] || templates.acknowledgement;
  return `${selectedTemplate}\n\nBest regards,\nBuildwit Technology`;
}

function getRecipientContext(context = {}) {
  const inquiry = context.inquiry || null;
  const sourceEmail = context.sourceEmail || null;

  return {
    recipientName: inquiry?.name || sourceEmail?.from_name || "there",
    companyName: inquiry?.company || sourceEmail?.company || "",
  };
}

function buildEnterpriseTemplate(templateKey, context = {}, includeDetails = true) {
  const inquiry = context.inquiry || null;
  const sourceEmail = context.sourceEmail || null;
  const { recipientName, companyName } = getRecipientContext(context);
  const productName = inquiry?.product_name || "your requirement";
  const companyReference = companyName ? ` from ${companyName}` : "";
  const detailsBlock = includeDetails && inquiry ? `\n\n${buildInquiryDetailsBlock(inquiry)}` : "";

  const templates = {
    executive_acknowledgement: `Dear ${recipientName},\n\nThank you for your message${companyReference}. We have reviewed your request related to ${productName} and logged it with our enterprise support desk.${detailsBlock}\n\nOur team will share a structured update with commercial and technical checkpoints in the next business cycle.`,
    qualification_request: `Dear ${recipientName},\n\nTo issue a complete proposal for ${productName}, we request a few inputs: required throughput/capacity, deployment location, utility availability, and target commissioning date.${detailsBlock}\n\nOnce received, we will deliver a fully scoped technical-commercial response.`,
    proposal_next_steps: `Dear ${recipientName},\n\nThank you for your continued engagement on ${productName}${companyReference}. We are preparing a proposal aligned to your operating priorities and implementation timeline.${detailsBlock}\n\nNext steps: technical confirmation, commercial alignment, and delivery planning workshop.`,
    stakeholder_meeting: `Dear ${recipientName},\n\nWe suggest a focused review call to align stakeholders on ${productName}, discuss key dependencies, and finalize execution milestones.${detailsBlock}\n\nPlease share 2-3 suitable slots and the participants from your side.`,
    escalation_path: `Dear ${recipientName},\n\nYour request has been escalated to our senior solutions team for accelerated action.${detailsBlock}\n\nYou will receive a priority response with ownership details and committed timelines shortly.`,
    professional_closure: `Dear ${recipientName},\n\nThank you for the interaction regarding ${productName}${companyReference}. We are closing this thread for now.${detailsBlock}\n\nIf priorities change, reply to this email and our team will reopen and assist immediately.`,
  };

  const selectedTemplate = templates[templateKey] || templates.executive_acknowledgement;
  return `${selectedTemplate}\n\nWarm regards,\nBuildwit Technology`;
}

function buildEmailSubject(inquiry) {
  const productName = inquiry?.product_name || "your inquiry";
  return `Buildwit Technology - Regarding ${productName}`;
}

function parseList(value) {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseSpecifications(value) {
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function sanitizeEmailHtml(rawHtml) {
  if (!rawHtml) return "";

  return rawHtml
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, "")
    .replace(/\son[a-z]+\s*=\s*(['"]).*?\1/gi, "")
    .replace(/\shref\s*=\s*(['"])javascript:[\s\S]*?\1/gi, ' href="#"');
}

function stripEmailHtmlToText(value) {
  if (!value) return "";

  const withoutBlocks = value.replace(/<(style|script)\b[^>]*>[\s\S]*?<\/\1>/gi, " ");
  const withoutTags = withoutBlocks.replace(/<[^>]+>/g, " ");
  return withoutTags.replace(/\s+/g, " ").trim();
}

function buildInboxEmailDocument(mailItem) {
  const hasHtml = Boolean(mailItem?.html_body && mailItem.html_body.trim());
  const content = hasHtml
    ? sanitizeEmailHtml(mailItem.html_body)
    : `<pre style="white-space:pre-wrap;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;color:#0f172a;font-size:13px;line-height:1.5">${(mailItem?.text_body || "No message content available.").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</pre>`;

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      body { margin: 0; background: #ffffff; color: #0f172a; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; }
      img { max-width: 100%; height: auto; }
      table { max-width: 100%; }
      a { word-break: break-word; }
    </style>
  </head>
  <body>${content}</body>
</html>`;
}

export default function Admin() {
  const [dashboard, setDashboard] = useState({ counts: {}, recent_inquiries: [] });
  const [productForm, setProductForm] = useState(initialProductForm);
  const [clientForm, setClientForm] = useState(initialClientForm);
  const [loginForm, setLoginForm] = useState(initialLoginForm);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [inquiriesSubTab, setInquiriesSubTab] = useState("manage");
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "" });
  const [editingProduct, setEditingProduct] = useState(null);
  const [editProductDialogOpen, setEditProductDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [inboxEmails, setInboxEmails] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [outboxEmails, setOutboxEmails] = useState([]);
  const [mailFolder, setMailFolder] = useState("inbox");
  const [clients, setClients] = useState([]);
  const [adminTestimonials, setAdminTestimonials] = useState([]);
  const [testimonialForm, setTestimonialForm] = useState(initialTestimonialForm);
  const [testimonialsLoading, setTestimonialsLoading] = useState(false);
  const [inquiriesLoading, setInquiriesLoading] = useState(false);
  const [mailboxLoading, setMailboxLoading] = useState(false);
  const [mailboxError, setMailboxError] = useState("");
  const [inboxUnreadOnly, setInboxUnreadOnly] = useState(false);
  const [inboxSearchInput, setInboxSearchInput] = useState("");
  const [inboxQuery, setInboxQuery] = useState("");
  const [inboxLastSyncedAt, setInboxLastSyncedAt] = useState("");
  const [selectedInboxEmail, setSelectedInboxEmail] = useState(null);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [inquiryStatusFilter, setInquiryStatusFilter] = useState("");
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [composeDialogOpen, setComposeDialogOpen] = useState(false);
  const [emailComposer, setEmailComposer] = useState(initialEmailComposer);
  const [emailSending, setEmailSending] = useState(false);
  const [homeHeroForm, setHomeHeroForm] = useState(initialHomeHeroForm);
  const [homeHeroLoading, setHomeHeroLoading] = useState(false);
  const [homeHeroSaving, setHomeHeroSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function loadCategories() {
      try {
        const data = await fetchCategories();
        if (!cancelled) {
          setCategories(Array.isArray(data) ? data : []);
        }
      } catch {
        if (!cancelled) {
          setCategories([]);
        }
      }
    }

    loadCategories();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function checkSession() {
      try {
        await fetchAdminMe();
        if (!cancelled) {
          setIsAuthenticated(true);
        }
      } catch {
        if (!cancelled) {
          logoutAdmin();
          setIsAuthenticated(false);
        }
      } finally {
        if (!cancelled) {
          setAuthLoading(false);
          setLoading(false);
        }
      }
    }

    checkSession();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || authLoading) {
      return;
    }

    let cancelled = false;

    async function loadDashboard() {
      try {
        const data = await fetchAdminDashboard();
        if (!cancelled) {
          setDashboard(data);
        }
      } catch (error) {
        const message = error?.response?.data?.detail || "Unable to load admin dashboard.";
        if (!cancelled) {
          setFeedback({ type: "error", message });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadDashboard();

    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || authLoading) {
      return;
    }

    let cancelled = false;

    async function loadHomeHeroContent() {
      setHomeHeroLoading(true);
      try {
        const data = await fetchAdminHomeHeroContent();
        if (!cancelled) {
          setHomeHeroForm({ ...initialHomeHeroForm, ...(data || {}) });
        }
      } catch {
        if (!cancelled) {
          setHomeHeroForm(initialHomeHeroForm);
        }
      } finally {
        if (!cancelled) {
          setHomeHeroLoading(false);
        }
      }
    }

    loadHomeHeroContent();

    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || authLoading || activeTab !== "inquiries" || inquiriesSubTab !== "manage") {
      return;
    }

    let cancelled = false;

    async function loadInquiries(silent = false) {
      if (!silent) {
        setInquiriesLoading(true);
      }

      try {
        const params = inquiryStatusFilter ? { status: inquiryStatusFilter } : {};
        const data = await fetchAdminInquiries(params);
        if (!cancelled) {
          setInquiries(Array.isArray(data) ? data : []);
        }
      } catch {
        if (!cancelled && !silent) {
          setInquiries([]);
        }
      } finally {
        if (!cancelled && !silent) {
          setInquiriesLoading(false);
        }
      }
    }

    loadInquiries();
    const intervalId = window.setInterval(() => {
      loadInquiries(true);
    }, INQUIRIES_POLL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [activeTab, authLoading, inquiriesSubTab, inquiryStatusFilter, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || authLoading || activeTab !== "inquiries" || inquiriesSubTab !== "inbox") {
      return;
    }

    let cancelled = false;

    async function loadMailboxEmails(silent = false) {
      if (!silent) {
        setMailboxLoading(true);
        setMailboxError("");
      }

      try {
        const params = { limit: 40 };
        if (mailFolder === "inbox") {
          params.unread_only = inboxUnreadOnly;
        }
        if (inboxQuery) {
          params.q = inboxQuery;
        }

        let data = [];
        if (mailFolder === "inbox") {
          data = await fetchAdminInboxEmails(params);
        } else if (mailFolder === "sent") {
          data = await fetchAdminSentEmails(params);
        } else {
          data = await fetchAdminOutboxEmails(params);
        }

        if (!cancelled) {
          const items = Array.isArray(data) ? data : [];
          if (mailFolder === "inbox") {
            setInboxEmails(items);
          } else if (mailFolder === "sent") {
            setSentEmails(items);
          } else {
            setOutboxEmails(items);
          }
          setInboxLastSyncedAt(new Date().toISOString());
        }
      } catch (error) {
        const message = error?.response?.data?.detail || "Unable to load mailbox right now.";
        if (!cancelled && !silent) {
          if (mailFolder === "inbox") {
            setInboxEmails([]);
          } else if (mailFolder === "sent") {
            setSentEmails([]);
          } else {
            setOutboxEmails([]);
          }
          setMailboxError(message);
        }
      } finally {
        if (!cancelled && !silent) {
          setMailboxLoading(false);
        }
      }
    }

    loadMailboxEmails();
    const intervalId = window.setInterval(() => {
      loadMailboxEmails(true);
    }, MAILBOX_POLL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [activeTab, authLoading, inboxQuery, inboxUnreadOnly, inquiriesSubTab, isAuthenticated, mailFolder]);

  useEffect(() => {
    if (!isAuthenticated || authLoading) {
      return;
    }

    let cancelled = false;

    async function loadClientsAndTestimonials() {
      setClientsLoading(true);
      setTestimonialsLoading(true);
      try {
        const [clientsData, testimonialsData] = await Promise.all([fetchAdminClients(), fetchAdminTestimonials()]);
        if (!cancelled) {
          setClients(Array.isArray(clientsData) ? clientsData : []);
          setAdminTestimonials(Array.isArray(testimonialsData) ? testimonialsData : []);
        }
      } catch {
        if (!cancelled) {
          setClients([]);
          setAdminTestimonials([]);
        }
      } finally {
        if (!cancelled) {
          setClientsLoading(false);
          setTestimonialsLoading(false);
        }
      }
    }

    loadClientsAndTestimonials();

    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || authLoading) {
      return;
    }

    let cancelled = false;

    async function loadProducts() {
      try {
        const data = await fetchProducts({ limit: 100 });
        if (!cancelled) {
          setProducts(Array.isArray(data) ? data : []);
        }
      } catch {
        if (!cancelled) {
          setProducts([]);
        }
      }
    }

    loadProducts();

    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated]);

  const summaryCards = useMemo(
    () => [
      { label: "Products", value: dashboard.counts.products ?? 0 },
      { label: "Clients", value: dashboard.counts.clients ?? 0 },
      { label: "Inquiries", value: dashboard.counts.inquiries ?? 0 },
      { label: "Categories", value: dashboard.counts.categories ?? 0 },
    ],
    [dashboard.counts],
  );

  const activeComposeInquiry = useMemo(
    () => inquiries.find((item) => item.id === emailComposer.inquiryId) || null,
    [emailComposer.inquiryId, inquiries],
  );

  const activeMailboxItems = useMemo(() => {
    if (mailFolder === "sent") {
      return sentEmails;
    }
    if (mailFolder === "outbox") {
      return outboxEmails;
    }
    return inboxEmails;
  }, [inboxEmails, mailFolder, outboxEmails, sentEmails]);

  const navItems = [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { key: "products", label: "Products", icon: Package },
    { key: "clients", label: "Clients", icon: Users },
    { key: "inquiries", label: "Inquiries", icon: MessageSquare },
    { key: "settings", label: "Settings", icon: Settings },
  ];

  function handleQuickAction(targetTab) {
    setActiveTab(targetTab);
    if (targetTab === "inquiries") {
      setInquiriesSubTab("manage");
    }
    setActionDialogOpen(false);
  }

  async function handleLoginSubmit(event) {
    event.preventDefault();
    setFeedback({ type: "", message: "" });

    const normalizedCredentials = {
      ...loginForm,
      username: String(loginForm.username || "").trim().toLowerCase(),
    };

    try {
      const data = await loginAdmin(normalizedCredentials);
      if (typeof window !== "undefined") {
        window.localStorage.setItem("admin_token", data.access_token);
      }
      setLoginForm(initialLoginForm);
      setIsAuthenticated(true);
      setFeedback({ type: "success", message: "Signed in successfully." });
    } catch (error) {
      const message = error?.response?.data?.detail || "Unable to sign in.";
      setFeedback({ type: "error", message });
    }
  }

  function handleLogout() {
    logoutAdmin();
    setIsAuthenticated(false);
    setDashboard({ counts: {}, recent_inquiries: [] });
    setProducts([]);
    setClients([]);
    setInquiries([]);
    setInboxEmails([]);
    setSentEmails([]);
    setOutboxEmails([]);
    setMailboxError("");
    setInboxSearchInput("");
    setInboxQuery("");
    setInboxUnreadOnly(false);
    setMailFolder("inbox");
    setInboxLastSyncedAt("");
    setEditingProduct(null);
    setEditProductDialogOpen(false);
    setHomeHeroForm(initialHomeHeroForm);
    setActiveTab("dashboard");
    setInquiriesSubTab("manage");
    setFeedback({ type: "success", message: "You have been signed out." });
  }

  async function handleHomeHeroContentSubmit(event) {
    event.preventDefault();
    setHomeHeroSaving(true);

    try {
      const toOptionalNumber = (value) => {
        if (value === "" || value === null || value === undefined) {
          return null;
        }

        const parsed = Number(value);
        if (!Number.isFinite(parsed) || parsed < 0) {
          return null;
        }

        return Math.floor(parsed);
      };

      const payload = {
        ...homeHeroForm,
        machines_delivered: toOptionalNumber(homeHeroForm.machines_delivered),
        enterprise_clients: toOptionalNumber(homeHeroForm.enterprise_clients),
        engineering_years: toOptionalNumber(homeHeroForm.engineering_years),
      };

      const updated = await updateAdminHomeHeroContent(payload);
      setHomeHeroForm({ ...initialHomeHeroForm, ...(updated || {}) });
      toast.success("Homepage hero content updated.", { duration: TOAST_DURATION });
    } catch (error) {
      const message = error?.response?.data?.detail || "Unable to update homepage hero content.";
      toast.error(message, { duration: TOAST_DURATION });
    } finally {
      setHomeHeroSaving(false);
    }
  }

  async function handlePasswordChange(event) {
    event.preventDefault();
    setFeedback({ type: "", message: "" });

    try {
      await changeAdminPassword(passwordForm);
      setPasswordForm({ current_password: "", new_password: "" });
      setFeedback({ type: "success", message: "Password changed successfully." });
    } catch (error) {
      const message = error?.response?.data?.detail || "Unable to change password.";
      setFeedback({ type: "error", message });
    }
  }

  function promptDeleteProduct(product) {
    setDeleteTarget({ kind: "product", id: product.slug, label: product.name || product.slug });
    setDeleteDialogOpen(true);
  }

  function promptDeleteClient(client) {
    setDeleteTarget({ kind: "client", id: client.id, label: client.name || client.id });
    setDeleteDialogOpen(true);
  }

  function closeDeleteDialog() {
    if (deleteLoading) {
      return;
    }

    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  }

  async function confirmDeleteTarget() {
    if (!deleteTarget) {
      return;
    }

    setDeleteLoading(true);
    setFeedback({ type: "", message: "" });

    try {
      if (deleteTarget.kind === "product") {
        await deleteAdminProduct(deleteTarget.id);
        const nextProducts = await fetchProducts({ limit: 100 });
        setProducts(Array.isArray(nextProducts) ? nextProducts : []);
        toast.success("Product deleted successfully.", { duration: TOAST_DURATION });
      } else {
        await deleteAdminClient(deleteTarget.id);
        const [nextDashboard, nextClients] = await Promise.all([fetchAdminDashboard(), fetchAdminClients()]);
        setDashboard(nextDashboard);
        setClients(Array.isArray(nextClients) ? nextClients : []);
        toast.success("Client deleted successfully.", { duration: TOAST_DURATION });
      }

      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (error) {
      if (error?.response?.status === 404 && deleteTarget.kind === "client") {
        const [nextDashboard, nextClients] = await Promise.all([fetchAdminDashboard(), fetchAdminClients()]);
        setDashboard(nextDashboard);
        setClients(Array.isArray(nextClients) ? nextClients : []);
        toast.error("Client not found. The list has been refreshed.", { duration: TOAST_DURATION });
      } else if (error?.response?.status === 404 && deleteTarget.kind === "product") {
        const nextProducts = await fetchProducts({ limit: 100 });
        setProducts(Array.isArray(nextProducts) ? nextProducts : []);
        toast.error("Product not found. The list has been refreshed.", { duration: TOAST_DURATION });
      } else {
        const message = error?.response?.data?.detail || `Unable to delete ${deleteTarget.kind}.`;
        toast.error(message, { duration: TOAST_DURATION });
      }
    } finally {
      setDeleteLoading(false);
    }
  }

  async function handleProductSubmit(event) {
    event.preventDefault();
    setFeedback({ type: "", message: "" });

    try {
      const payload = {
        ...productForm,
        industries: parseList(productForm.industries),
        images: Array.isArray(productForm.images) ? productForm.images : [],
        detail_images: Array.isArray(productForm.detail_images) ? productForm.detail_images : [],
        advantages: parseList(productForm.advantages),
        applications: parseList(productForm.applications),
        specifications: parseSpecifications(productForm.specifications),
      };

      if (editingProduct) {
        await updateAdminProduct(editingProduct.slug, payload);
        toast.success("Product updated successfully.", { duration: TOAST_DURATION });
        setEditProductDialogOpen(false);
      } else {
        await createAdminProduct(payload);
        toast.success("Product uploaded successfully.", { duration: TOAST_DURATION });
      }
      setProductForm(initialProductForm);
      setEditingProduct(null);
      const nextDashboard = await fetchAdminDashboard();
      const nextProducts = await fetchProducts({ limit: 100 });
      setDashboard(nextDashboard);
      setProducts(Array.isArray(nextProducts) ? nextProducts : []);
    } catch (error) {
      if (error?.response?.status === 401) {
        logoutAdmin();
        setIsAuthenticated(false);
        toast.error("Your session expired. Please sign in again.", { duration: TOAST_DURATION });
        return;
      }

      const message = error?.response?.data?.detail || "Unable to upload product.";
      toast.error(message, { duration: TOAST_DURATION });
    }
  }

  async function handleClientSubmit(event) {
    event.preventDefault();
    setFeedback({ type: "", message: "" });

    try {
      await createAdminClient(clientForm);
      toast.success("Client added successfully.", { duration: TOAST_DURATION });
      setClientForm(initialClientForm);
      const [nextDashboard, nextClients] = await Promise.all([fetchAdminDashboard(), fetchAdminClients()]);
      setDashboard(nextDashboard);
      setClients(Array.isArray(nextClients) ? nextClients : []);
    } catch (error) {
      if (error?.response?.status === 401) {
        logoutAdmin();
        setIsAuthenticated(false);
        toast.error("Your session expired. Please sign in again.", { duration: TOAST_DURATION });
        return;
      }

      const message = error?.response?.data?.detail || "Unable to add client.";
      toast.error(message, { duration: TOAST_DURATION });
    }
  }

  async function handleTestimonialSubmit(event) {
    event.preventDefault();
    setFeedback({ type: "", message: "" });

    try {
      await createAdminTestimonial({
        ...testimonialForm,
        quote: testimonialForm.quote.trim(),
        designation: testimonialForm.designation.trim(),
        rating: Number(testimonialForm.rating) || 5,
      });
      toast.success("Testimonial added successfully.", { duration: TOAST_DURATION });
      setTestimonialForm(initialTestimonialForm);
      const nextTestimonials = await fetchAdminTestimonials();
      setAdminTestimonials(Array.isArray(nextTestimonials) ? nextTestimonials : []);
    } catch (error) {
      if (error?.response?.status === 401) {
        logoutAdmin();
        setIsAuthenticated(false);
        toast.error("Your session expired. Please sign in again.", { duration: TOAST_DURATION });
        return;
      }

      const message = error?.response?.data?.detail || "Unable to add testimonial.";
      toast.error(message, { duration: TOAST_DURATION });
    }
  }

  async function handleDeleteTestimonial(testimonialId) {
    try {
      await deleteAdminTestimonial(testimonialId);
      const nextTestimonials = await fetchAdminTestimonials();
      setAdminTestimonials(Array.isArray(nextTestimonials) ? nextTestimonials : []);
      toast.success("Testimonial removed.", { duration: TOAST_DURATION });
    } catch (error) {
      const message = error?.response?.data?.detail || "Unable to remove testimonial.";
      toast.error(message, { duration: TOAST_DURATION });
    }
  }

  async function handleProductImageUpload(event) {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const validFiles = Array.from(files).filter(isValidImageFile);
    if (validFiles.length === 0) {
      toast.error("Please select valid image files (JPEG, PNG, WebP, GIF)", { duration: TOAST_DURATION });
      return;
    }

    try {
      const base64Images = await filesToBase64(validFiles);
      setProductForm({
        ...productForm,
        images: base64Images,
      });
      toast.success(`${validFiles.length} image(s) uploaded.`, { duration: TOAST_DURATION });
    } catch {
      toast.error("Failed to process images.", { duration: TOAST_DURATION });
    }
  }

  async function handleClientLogoUpload(event) {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (!isValidImageFile(file)) {
      toast.error("Please select a valid image file (JPEG, PNG, WebP, GIF)", { duration: TOAST_DURATION });
      return;
    }

    try {
      const base64Logo = await fileToBase64(file);
      setClientForm({
        ...clientForm,
        logo: base64Logo,
      });
      toast.success("Logo uploaded.", { duration: TOAST_DURATION });
    } catch {
      toast.error("Failed to process logo.", { duration: TOAST_DURATION });
    }
  }

  async function handleProductDetailChartUpload(event) {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const validFiles = Array.from(files).filter(isValidImageFile);
    if (validFiles.length === 0) {
      toast.error("Please select valid chart image files (JPEG, PNG, WebP, GIF)", { duration: TOAST_DURATION });
      return;
    }

    try {
      const chartImages = await filesToBase64(validFiles);
      setProductForm((prev) => ({
        ...prev,
        detail_images: [...(prev.detail_images || []), ...chartImages],
      }));
      toast.success(`${validFiles.length} chart image(s) uploaded.`, { duration: TOAST_DURATION });
    } catch {
      toast.error("Failed to process chart images.", { duration: TOAST_DURATION });
    } finally {
      event.target.value = "";
    }
  }

  function startEditingProduct(product) {
    setEditingProduct(product);
    setProductForm({
      slug: product.slug,
      name: product.name,
      tagline: product.tagline || "",
      description: product.description || "",
      category_slug: product.category_slug || "",
      industries: Array.isArray(product.industries) ? product.industries.join(", ") : "",
      images: Array.isArray(product.images) ? product.images : [],
      detail_images: Array.isArray(product.detail_images) ? product.detail_images : [],
      specifications: JSON.stringify(product.specifications || [{ key: "Capacity", value: "Custom" }], null, 2),
      advantages: Array.isArray(product.advantages) ? product.advantages.join(", ") : "",
      applications: Array.isArray(product.applications) ? product.applications.join(", ") : "",
      featured: Boolean(product.featured),
      price_from: product.price_from || "",
    });
    setActiveTab("products");
    setEditProductDialogOpen(true);
  }

  function cancelProductEditing() {
    setEditProductDialogOpen(false);
    setEditingProduct(null);
    setProductForm(initialProductForm);
  }

  function removeProductImage(index) {
    const updatedImages = productForm.images.filter((_, i) => i !== index);
    setProductForm({ ...productForm, images: updatedImages });
  }

  function removeProductDetailImage(index) {
    setProductForm((prev) => ({
      ...prev,
      detail_images: (prev.detail_images || []).filter((_, i) => i !== index),
    }));
  }

  function openQuickComposeDialog() {
    setEmailComposer({
      ...initialEmailComposer,
      mode: "mailbox",
      subject: "Buildwit Technology - Inquiry follow up",
      body: "",
      includeDetails: false,
    });
    setComposeDialogOpen(true);
  }

  async function refreshDashboardAndInquiries() {
    const [nextDashboard, nextInquiries] = await Promise.all([
      fetchAdminDashboard(),
      fetchAdminInquiries(inquiryStatusFilter ? { status: inquiryStatusFilter } : {}),
    ]);
    setDashboard(nextDashboard);
    setInquiries(Array.isArray(nextInquiries) ? nextInquiries : []);
  }

  async function handleInquiryStatusChange(inquiry, status) {
    setFeedback({ type: "", message: "" });

    try {
      await updateAdminInquiry(inquiry.id, { status });
      await refreshDashboardAndInquiries();
      toast.success("Inquiry status updated.", { duration: TOAST_DURATION });
    } catch (error) {
      const message = error?.response?.data?.detail || "Unable to update inquiry status.";
      toast.error(message, { duration: TOAST_DURATION });
    }
  }

  async function handleDeleteInquiry(inquiryId) {
    if (!window.confirm("Delete this inquiry?")) return;

    setFeedback({ type: "", message: "" });

    try {
      await deleteAdminInquiry(inquiryId);
      await refreshDashboardAndInquiries();
      toast.success("Inquiry deleted successfully.", { duration: TOAST_DURATION });
    } catch (error) {
      const message = error?.response?.data?.detail || "Unable to delete inquiry.";
      toast.error(message, { duration: TOAST_DURATION });
    }
  }

  function handleInboxSearchSubmit(event) {
    event.preventDefault();
    setInboxQuery(inboxSearchInput.trim());
  }

  async function refreshInboxEmails() {
    if (!isAuthenticated || authLoading) {
      return;
    }

    setMailboxLoading(true);
    setMailboxError("");
    try {
      const params = { limit: 40 };
      if (mailFolder === "inbox") {
        params.unread_only = inboxUnreadOnly;
      }
      if (inboxQuery) {
        params.q = inboxQuery;
      }

      let data = [];
      if (mailFolder === "inbox") {
        data = await fetchAdminInboxEmails(params);
        setInboxEmails(Array.isArray(data) ? data : []);
      } else if (mailFolder === "sent") {
        data = await fetchAdminSentEmails(params);
        setSentEmails(Array.isArray(data) ? data : []);
      } else {
        data = await fetchAdminOutboxEmails(params);
        setOutboxEmails(Array.isArray(data) ? data : []);
      }

      setInboxLastSyncedAt(new Date().toISOString());
    } catch (error) {
      const message = error?.response?.data?.detail || "Unable to load mailbox right now.";
      setMailboxError(message);
    } finally {
      setMailboxLoading(false);
    }
  }

  function handleComposeEmail(inquiry) {
    if (!inquiry?.email) {
      toast.error("This inquiry does not have an email address.", { duration: TOAST_DURATION });
      return;
    }

    setEmailComposer({
      inquiryId: inquiry.id,
      to: inquiry.email,
      cc: "",
      subject: buildEmailSubject(inquiry),
      body: buildEnterpriseTemplate("executive_acknowledgement", { inquiry }, true),
      templateKey: "executive_acknowledgement",
      includeDetails: true,
      mode: "inquiry",
      sourceEmail: null,
    });
    setComposeDialogOpen(true);
  }

  async function handleAttachmentSelection(event) {
    const files = Array.from(event.target.files || []);
    if (!files.length) {
      return;
    }

    try {
      const convertedAttachments = await Promise.all(
        files.map(async (file) => {
          const base64Value = await fileToBase64(file);
          const contentBase64 = base64Value.includes(",") ? base64Value.split(",")[1] : base64Value;
          return {
            filename: file.name,
            contentBase64,
            contentType: file.type || "application/octet-stream",
          };
        }),
      );

      setEmailComposer((prev) => ({
        ...prev,
        attachments: [...(prev.attachments || []), ...convertedAttachments],
      }));
    } catch {
      toast.error("Unable to read selected files. Please try again.", { duration: TOAST_DURATION });
    } finally {
      event.target.value = "";
    }
  }

  function removeAttachment(index) {
    setEmailComposer((prev) => ({
      ...prev,
      attachments: (prev.attachments || []).filter((_, itemIndex) => itemIndex !== index),
    }));
  }

  function handleReplyToMailboxEmail(mailItem) {
    const recipient = mailItem?.from_email || "";
    if (!recipient) {
      toast.error("Sender email is not available for this message.", { duration: TOAST_DURATION });
      return;
    }

    const cleanSubject = (mailItem?.subject || "").trim();
    const replySubject = /^re:/i.test(cleanSubject) ? cleanSubject : `Re: ${cleanSubject || "Your email"}`;
    setEmailComposer({
      inquiryId: "",
      to: recipient,
      cc: "",
      subject: replySubject,
      body: buildEnterpriseTemplate("executive_acknowledgement", { sourceEmail: mailItem }, true),
      templateKey: "executive_acknowledgement",
      includeDetails: true,
      mode: "mailbox",
      sourceEmail: mailItem,
    });
    setComposeDialogOpen(true);
  }

  function handleTemplateChange(templateKey) {
    setEmailComposer((prev) => ({
      ...prev,
      templateKey,
      body:
        prev.mode === "inquiry"
          ? buildEnterpriseTemplate(templateKey, { inquiry: activeComposeInquiry }, prev.includeDetails)
          : buildEnterpriseTemplate(templateKey, { sourceEmail: prev.sourceEmail }, prev.includeDetails),
    }));
  }

  function toggleIncludeDetails(checked) {
    setEmailComposer((prev) => ({
      ...prev,
      includeDetails: checked,
      body:
        prev.mode === "inquiry"
          ? buildEnterpriseTemplate(prev.templateKey, { inquiry: activeComposeInquiry }, checked)
          : buildEnterpriseTemplate(prev.templateKey, { sourceEmail: prev.sourceEmail }, checked),
    }));
  }

  async function sendEmailReply() {
    if (!emailComposer.to.trim()) {
      toast.error("Recipient email is required to compose the message.", { duration: TOAST_DURATION });
      return;
    }

    const payload = {
      to: emailComposer.to.trim(),
      cc: emailComposer.cc.trim() || null,
      subject: emailComposer.subject.trim() || "Buildwit Technology - Inquiry follow up",
      body: emailComposer.body.trim(),
      attachments: (emailComposer.attachments || []).map((attachment) => ({
        filename: attachment.filename,
        contentBase64: attachment.contentBase64,
        contentType: attachment.contentType,
      })),
    };

    if (!payload.body) {
      toast.error("Email body cannot be empty.", { duration: TOAST_DURATION });
      return;
    }

    setEmailSending(true);
    try {
      let response;
      if (emailComposer.mode === "inquiry") {
        if (!emailComposer.inquiryId) {
          toast.error("Unable to determine inquiry reference.", { duration: TOAST_DURATION });
          return;
        }
        response = await sendAdminInquiryEmail(emailComposer.inquiryId, payload);
        await refreshDashboardAndInquiries();
      } else {
        response = await sendAdminMailboxEmail({
          ...payload,
          source: "mailbox_reply",
          inquiry_id: null,
        });
      }

      toast.success(response?.message || "Email sent successfully.", { duration: TOAST_DURATION });
      setComposeDialogOpen(false);
      setEmailComposer(initialEmailComposer);
      setMailFolder("sent");
      await refreshInboxEmails();
    } catch (error) {
      const message = error?.response?.data?.detail || "Unable to send email right now.";
      toast.error(message, { duration: TOAST_DURATION });
    } finally {
      setEmailSending(false);
    }
  }

  async function saveCurrentEmailAsDraft() {
    const draftPayload = {
      to: emailComposer.to.trim(),
      cc: emailComposer.cc.trim(),
      subject: emailComposer.subject.trim(),
      body: emailComposer.body,
    };

    if (!draftPayload.body.trim() && !draftPayload.subject) {
      toast.error("Add at least a subject or message before saving draft.", { duration: TOAST_DURATION });
      return;
    }

    try {
      await saveAdminOutboxDraft(draftPayload);
      toast.success("Draft saved to Outbox.", { duration: TOAST_DURATION });
      setMailFolder("outbox");
      await refreshInboxEmails();
    } catch (error) {
      const message = error?.response?.data?.detail || "Unable to save draft.";
      toast.error(message, { duration: TOAST_DURATION });
    }
  }

  function openOutboxDraft(draftEmail) {
    setEmailComposer({
      inquiryId: "",
      to: draftEmail?.to_email || "",
      cc: draftEmail?.cc_email || "",
      subject: draftEmail?.subject === "(Draft)" ? "" : draftEmail?.subject || "",
      body: draftEmail?.text_body || draftEmail?.preview || "",
      templateKey: "executive_acknowledgement",
      includeDetails: false,
      mode: "mailbox",
      sourceEmail: null,
    });
    setComposeDialogOpen(true);
  }

  async function removeOutboxDraft(draftId) {
    try {
      await deleteAdminOutboxDraft(draftId);
      toast.success("Draft removed from outbox.", { duration: TOAST_DURATION });
      if (selectedInboxEmail?.id === draftId) {
        setSelectedInboxEmail(null);
      }
      await refreshInboxEmails();
    } catch (error) {
      const message = error?.response?.data?.detail || "Unable to remove draft.";
      toast.error(message, { duration: TOAST_DURATION });
    }
  }

  async function copyEmailDraft() {
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(`To: ${emailComposer.to}\nCC: ${emailComposer.cc || "-"}\nSubject: ${emailComposer.subject}\n\n${emailComposer.body}`);
        toast.success("Email draft copied to clipboard.", { duration: TOAST_DURATION });
      }
    } catch {
      toast.error("Unable to copy email draft.", { duration: TOAST_DURATION });
    }
  }

  function formatDateTime(value) {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString();
  }

  function formatEmailDateTime(value) {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
  }

  function getInboxPreview(mailItem) {
    const textBody = (mailItem?.text_body || "").trim();
    if (textBody) {
      return textBody.replace(/\s+/g, " ").slice(0, 260);
    }

    const htmlSource = (mailItem?.html_body || "").trim();
    if (htmlSource) {
      return stripEmailHtmlToText(htmlSource).slice(0, 260);
    }

    return (mailItem?.preview || "No preview available.").trim();
  }

  if (authLoading) {
    return (
      <div className="mx-auto flex max-w-2xl items-center justify-center px-4 py-20">
        <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-brand-orange">Admin Access</p>
          <h1 className="mt-3 text-2xl font-semibold text-slate-900">Checking your session…</h1>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="mx-auto flex max-w-2xl px-4 py-20">
        <div className="w-full rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-brand-orange">Admin Portal</p>
          <h1 className="mt-3 text-3xl font-semibold text-slate-900">Secure sign in</h1>
          <p className="mt-3 text-sm text-slate-600">
            Enter your admin credentials to access the operations dashboard.
          </p>

          {feedback.message ? (
            <div className={`mt-6 rounded-2xl border px-4 py-3 text-sm ${feedback.type === "success" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-red-200 bg-red-50 text-red-700"}`}>
              {feedback.message}
            </div>
          ) : null}

          <form onSubmit={handleLoginSubmit} className="mt-6 grid gap-4">
            <Input
              type="email"
              placeholder="Admin email"
              value={loginForm.username}
              onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
              required
            />
            <Input
              type="password"
              placeholder="Password"
              value={loginForm.password}
              onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
              required
            />
            <Button type="submit" className="w-full bg-brand-orange text-white hover:bg-orange-600">
              Sign in
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-8 px-4 py-10 lg:px-8">
      <div className="overflow-hidden rounded-[32px] border border-slate-200 bg-gradient-to-br from-white via-orange-50/50 to-slate-50 p-6 shadow-[0_18px_60px_-24px_rgba(15,23,42,0.35)]">
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg shadow-orange-200/50">
                <img src="/Logonew.png" alt="Buildwit Technology" className="h-9 w-9 object-contain" />
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-orange">Buildwit technology</p>
                <h1 className="text-xl font-semibold text-slate-900">Admin control centre</h1>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
                <DialogTrigger asChild>
                  <Button type="button" variant="secondary" className="px-4">
                    <Zap className="h-4 w-4" />
                    Quick actions
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="text-xl">Admin shortcuts</DialogTitle>
                    <DialogDescription>Jump straight to the most-used admin tools from one polished workspace.</DialogDescription>
                  </DialogHeader>

                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <button type="button" onClick={() => handleQuickAction("dashboard")} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-left transition hover:-translate-y-0.5 hover:border-orange-200 hover:bg-orange-50">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-brand-orange shadow-sm">
                        <Package2 className="h-5 w-5" />
                      </div>
                      <p className="mt-3 font-semibold text-slate-900">Create or update a product</p>
                      <p className="mt-1 text-sm text-slate-600">Manage product details, media and pricing from one place.</p>
                    </button>
                    <button type="button" onClick={() => handleQuickAction("clients")} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-left transition hover:-translate-y-0.5 hover:border-orange-200 hover:bg-orange-50">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-brand-orange shadow-sm">
                        <UserPlus className="h-5 w-5" />
                      </div>
                      <p className="mt-3 font-semibold text-slate-900">Add a new client</p>
                      <p className="mt-1 text-sm text-slate-600">Keep partner companies and their details organized.</p>
                    </button>
                    <button type="button" onClick={() => handleQuickAction("products")} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-left transition hover:-translate-y-0.5 hover:border-orange-200 hover:bg-orange-50">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-brand-orange shadow-sm">
                        <Sparkles className="h-5 w-5" />
                      </div>
                      <p className="mt-3 font-semibold text-slate-900">Manage existing products</p>
                      <p className="mt-1 text-sm text-slate-600">Review, update or remove current product entries quickly.</p>
                    </button>
                    <button type="button" onClick={() => handleQuickAction("inquiries")} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-left transition hover:-translate-y-0.5 hover:border-orange-200 hover:bg-orange-50">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-brand-orange shadow-sm">
                        <Package2 className="h-5 w-5" />
                      </div>
                      <p className="mt-3 font-semibold text-slate-900">Manage inquiries</p>
                      <p className="mt-1 text-sm text-slate-600">Track, update and clear customer enquiries from one place.</p>
                    </button>
                    <button type="button" onClick={() => handleQuickAction("settings")} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-left transition hover:-translate-y-0.5 hover:border-orange-200 hover:bg-orange-50">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-brand-orange shadow-sm">
                        <ShieldCheck className="h-5 w-5" />
                      </div>
                      <p className="mt-3 font-semibold text-slate-900">Change admin password</p>
                      <p className="mt-1 text-sm text-slate-600">Secure the account with fresh credentials when needed.</p>
                    </button>
                  </div>
                </DialogContent>
              </Dialog>

              <Button type="button" variant="default" className="bg-slate-900 px-4 text-white hover:bg-slate-700" onClick={handleLogout}>
                <LogOut className="h-4 w-4" />
                Sign out
              </Button>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white/80 p-2 shadow-sm backdrop-blur">
            <div className="flex flex-wrap gap-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.key;
                return (
                  <Button
                    key={item.key}
                    type="button"
                    variant={isActive ? "primary" : "ghost"}
                    className={isActive ? "px-4" : "px-4 text-slate-700 hover:bg-slate-100"}
                    onClick={() => setActiveTab(item.key)}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {activeTab === "dashboard" && (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            {summaryCards.map((card) => (
              <div key={card.label} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-sm text-slate-500">{card.label}</p>
                <p className="mt-2 text-3xl font-semibold text-slate-900">{card.value}</p>
              </div>
            ))}
          </div>

          <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-slate-900">Upload product</h2>
              <p className="mt-1 text-sm text-slate-500">Create a new product entry with details and media.</p>

              <form onSubmit={handleProductSubmit} className="mt-6 grid gap-4 md:grid-cols-2">
            <Input placeholder="Slug" value={productForm.slug} onChange={(e) => setProductForm({ ...productForm, slug: e.target.value })} required />
            <Input placeholder="Product name" value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} required />
            <Input placeholder="Tagline" value={productForm.tagline} onChange={(e) => setProductForm({ ...productForm, tagline: e.target.value })} />
            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-medium text-slate-700">Category</label>
              <select
                value={productForm.category_slug}
                onChange={(e) => setProductForm({ ...productForm, category_slug: e.target.value })}
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm outline-none"
                required
              >
                <option value="">Select a category</option>
                {categories.map((category) => (
                  <option key={category.slug} value={category.slug}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <Input placeholder="Price from" value={productForm.price_from} onChange={(e) => setProductForm({ ...productForm, price_from: e.target.value })} />
            <label className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-600">
              <input type="checkbox" checked={productForm.featured} onChange={(e) => setProductForm({ ...productForm, featured: e.target.checked })} />
              Featured product
            </label>
            <Input placeholder="Industries (comma separated)" value={productForm.industries} onChange={(e) => setProductForm({ ...productForm, industries: e.target.value })} />

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">Product Images</label>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleProductImageUpload}
                className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-brand-orange file:text-white hover:file:bg-orange-600"
              />
              <p className="mt-1 text-xs text-slate-500">Supported: JPEG, PNG, WebP, GIF</p>

              {productForm.images && productForm.images.length > 0 && (
                <div className="mt-4 grid grid-cols-4 gap-3">
                  {productForm.images.map((img, idx) => (
                    <div key={idx} className="relative rounded-lg overflow-hidden border border-slate-200 aspect-[4/3]">
                      <img src={img} alt={`Product ${idx + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeProductImage(idx)}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">Product Detail Chart Images</label>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleProductDetailChartUpload}
                className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-slate-900 file:text-white hover:file:bg-slate-700"
              />
              <p className="mt-1 text-xs text-slate-500">Upload detailed product charts/technical visuals.</p>

              {productForm.detail_images && productForm.detail_images.length > 0 && (
                <div className="mt-4 grid grid-cols-4 gap-3">
                  {productForm.detail_images.map((img, idx) => (
                    <div key={`detail-${idx}`} className="relative rounded-lg overflow-hidden border border-slate-200 aspect-[4/3]">
                      <img src={img} alt={`Chart ${idx + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeProductDetailImage(idx)}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Input placeholder="Advantages (comma separated)" value={productForm.advantages} onChange={(e) => setProductForm({ ...productForm, advantages: e.target.value })} />
            <Input placeholder="Applications (comma separated)" value={productForm.applications} onChange={(e) => setProductForm({ ...productForm, applications: e.target.value })} />
            <textarea
              className="min-h-[96px] rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm outline-none md:col-span-2"
              placeholder='Specifications JSON: [{"key":"Capacity","value":"500 L"}]'
              value={productForm.specifications}
              onChange={(e) => setProductForm({ ...productForm, specifications: e.target.value })}
            />
            <textarea
              className="min-h-[110px] rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm outline-none md:col-span-2"
              placeholder="Description"
              value={productForm.description}
              onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
              required
            />
            <div className="md:col-span-2">
              <Button type="submit" className="w-full bg-brand-orange text-white hover:bg-orange-600" disabled={loading}>
                {loading ? "Saving..." : editingProduct ? "Update product" : "Save product"}
              </Button>
            </div>
          </form>
        </div>

        <div className="space-y-6">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Homepage hero content</h2>
                <p className="mt-1 text-sm text-slate-500">Update latest delivery details and the floating stats bar shown below the hero section.</p>
              </div>
              <span className="rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-brand-orange">
                Live
              </span>
            </div>

            <form onSubmit={handleHomeHeroContentSubmit} className="mt-5 grid gap-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Latest delivery badge</label>
                  <Input
                    value={homeHeroForm.latest_delivery_overline}
                    onChange={(event) => setHomeHeroForm((prev) => ({ ...prev, latest_delivery_overline: event.target.value }))}
                    placeholder="Latest delivery"
                    disabled={homeHeroLoading || homeHeroSaving}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Latest delivery headline</label>
                  <Input
                    value={homeHeroForm.latest_delivery_title}
                    onChange={(event) => setHomeHeroForm((prev) => ({ ...prev, latest_delivery_title: event.target.value }))}
                    placeholder="Robotic MIG welding cell shipped to a Tier-1 auto major"
                    disabled={homeHeroLoading || homeHeroSaving}
                  />
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Cycle time</label>
                  <Input
                    value={homeHeroForm.cycle_time}
                    onChange={(event) => setHomeHeroForm((prev) => ({ ...prev, cycle_time: event.target.value }))}
                    placeholder="42 sec"
                    disabled={homeHeroLoading || homeHeroSaving}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Uptime</label>
                  <Input
                    value={homeHeroForm.uptime}
                    onChange={(event) => setHomeHeroForm((prev) => ({ ...prev, uptime: event.target.value }))}
                    placeholder="99.3%"
                    disabled={homeHeroLoading || homeHeroSaving}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Weld quality</label>
                  <Input
                    value={homeHeroForm.weld_quality}
                    onChange={(event) => setHomeHeroForm((prev) => ({ ...prev, weld_quality: event.target.value }))}
                    placeholder="Cat A"
                    disabled={homeHeroLoading || homeHeroSaving}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Payback</label>
                  <Input
                    value={homeHeroForm.payback}
                    onChange={(event) => setHomeHeroForm((prev) => ({ ...prev, payback: event.target.value }))}
                    placeholder="14 months"
                    disabled={homeHeroLoading || homeHeroSaving}
                  />
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-semibold text-slate-900">Floating hero stats</p>
                <p className="mt-1 text-xs text-slate-500">These values appear in the bottom floating bar of the home hero section.</p>

                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Machines delivered</label>
                    <Input
                      type="number"
                      min="0"
                      value={homeHeroForm.machines_delivered}
                      onChange={(event) => setHomeHeroForm((prev) => ({ ...prev, machines_delivered: event.target.value }))}
                      disabled={homeHeroLoading || homeHeroSaving}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Enterprise clients</label>
                    <Input
                      type="number"
                      min="0"
                      value={homeHeroForm.enterprise_clients}
                      onChange={(event) => setHomeHeroForm((prev) => ({ ...prev, enterprise_clients: event.target.value }))}
                      disabled={homeHeroLoading || homeHeroSaving}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Engineering years</label>
                    <Input
                      type="number"
                      min="0"
                      value={homeHeroForm.engineering_years}
                      onChange={(event) => setHomeHeroForm((prev) => ({ ...prev, engineering_years: event.target.value }))}
                      disabled={homeHeroLoading || homeHeroSaving}
                    />
                  </div>
                </div>
              </div>

              <div>
                <Button type="submit" className="w-full bg-slate-900 text-white hover:bg-slate-700" disabled={homeHeroLoading || homeHeroSaving}>
                  {homeHeroSaving ? "Updating hero content..." : "Save homepage hero content"}
                </Button>
              </div>
            </form>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-slate-900">Client management</h2>
            <p className="mt-1 text-sm text-slate-500">Add and remove client profiles from the dedicated Clients workspace.</p>

            <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm text-slate-600">Current registered clients</p>
              <p className="mt-1 text-3xl font-semibold text-slate-900">{dashboard.counts.clients ?? 0}</p>
              <Button type="button" className="mt-4 w-full bg-slate-900 text-white hover:bg-slate-700" onClick={() => setActiveTab("clients")}>Open clients tab</Button>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-slate-900">Recent enquiries</h2>
            <div className="mt-4 space-y-3">
              {dashboard.recent_inquiries.length === 0 ? (
                <p className="text-sm text-slate-500">No enquiries yet.</p>
              ) : (
                dashboard.recent_inquiries.map((item) => (
                  <div key={`${item.name}-${item.created_at}`} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <p className="font-medium text-slate-900">{item.name}</p>
                    <p className="text-sm text-slate-600">{item.email}</p>
                    <p className="mt-1 text-xs text-slate-500">{item.product_name || "General enquiry"}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
          </div>
        </>
      )}

      {activeTab === "products" && (
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Manage products</h2>
              <p className="mt-1 text-sm text-slate-500">Edit or remove existing products.</p>
            </div>
            <Button type="button" className="bg-brand-orange text-white hover:bg-orange-600" onClick={() => setActiveTab("dashboard")}>Add product</Button>
          </div>

          <div className="mt-6 space-y-3">
            {products.length === 0 ? (
              <p className="text-sm text-slate-500">No products available.</p>
            ) : (
              products.map((product) => (
                <div key={product.slug} className="flex flex-col gap-3 rounded-2xl border border-slate-200 p-4 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-3">
                    <div className="relative h-14 w-20 overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                      {Array.isArray(product.images) && product.images.length > 0 ? (
                        <img src={product.images[0]} alt={product.name} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          No image
                        </div>
                      )}
                      {Array.isArray(product.images) && product.images.length > 1 ? (
                        <span className="absolute bottom-1 right-1 rounded-full bg-slate-900/85 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                          +{product.images.length - 1}
                        </span>
                      ) : null}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{product.name}</p>
                      <p className="text-sm text-slate-600">{product.category_slug || "Uncategorized"}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" className="border-slate-200 text-slate-700" onClick={() => startEditingProduct(product)}>Edit</Button>
                    <Button type="button" className="bg-red-600 text-white hover:bg-red-700" onClick={() => promptDeleteProduct(product)}>Delete</Button>
                  </div>
                </div>
              ))
            )}
          </div>

          <Dialog open={editProductDialogOpen} onOpenChange={(open) => (open ? setEditProductDialogOpen(true) : cancelProductEditing())}>
            <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl">Edit product</DialogTitle>
                <DialogDescription>Update product details and save changes without leaving this page.</DialogDescription>
              </DialogHeader>

              <form onSubmit={handleProductSubmit} className="mt-4 grid gap-4 md:grid-cols-2">
                <Input placeholder="Slug" value={productForm.slug} onChange={(e) => setProductForm({ ...productForm, slug: e.target.value })} required />
                <Input placeholder="Product name" value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} required />
                <Input placeholder="Tagline" value={productForm.tagline} onChange={(e) => setProductForm({ ...productForm, tagline: e.target.value })} />
                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-slate-700">Category</label>
                  <select
                    value={productForm.category_slug}
                    onChange={(e) => setProductForm({ ...productForm, category_slug: e.target.value })}
                    className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm outline-none"
                    required
                  >
                    <option value="">Select a category</option>
                    {categories.map((category) => (
                      <option key={category.slug} value={category.slug}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <Input placeholder="Price from" value={productForm.price_from} onChange={(e) => setProductForm({ ...productForm, price_from: e.target.value })} />
                <label className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-600">
                  <input type="checkbox" checked={productForm.featured} onChange={(e) => setProductForm({ ...productForm, featured: e.target.checked })} />
                  Featured product
                </label>
                <Input placeholder="Industries (comma separated)" value={productForm.industries} onChange={(e) => setProductForm({ ...productForm, industries: e.target.value })} />

                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-slate-700">Product Images</label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleProductImageUpload}
                    className="block w-full text-sm text-slate-500 file:mr-4 file:rounded-md file:border-0 file:bg-brand-orange file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-orange-600"
                  />
                  <p className="mt-1 text-xs text-slate-500">Supported: JPEG, PNG, WebP, GIF</p>

                  {productForm.images && productForm.images.length > 0 && (
                    <div className="mt-4 grid grid-cols-4 gap-3">
                      {productForm.images.map((img, idx) => (
                        <div key={idx} className="relative aspect-[4/3] overflow-hidden rounded-lg border border-slate-200">
                          <img src={img} alt={`Product ${idx + 1}`} className="h-full w-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeProductImage(idx)}
                            className="absolute right-1 top-1 rounded-full bg-red-500 p-1 text-white hover:bg-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-slate-700">Product Detail Chart Images</label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleProductDetailChartUpload}
                    className="block w-full text-sm text-slate-500 file:mr-4 file:rounded-md file:border-0 file:bg-slate-900 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-slate-700"
                  />
                  <p className="mt-1 text-xs text-slate-500">Upload detailed product charts/technical visuals.</p>

                  {productForm.detail_images && productForm.detail_images.length > 0 && (
                    <div className="mt-4 grid grid-cols-4 gap-3">
                      {productForm.detail_images.map((img, idx) => (
                        <div key={`edit-detail-${idx}`} className="relative aspect-[4/3] overflow-hidden rounded-lg border border-slate-200">
                          <img src={img} alt={`Chart ${idx + 1}`} className="h-full w-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeProductDetailImage(idx)}
                            className="absolute right-1 top-1 rounded-full bg-red-500 p-1 text-white hover:bg-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Input placeholder="Advantages (comma separated)" value={productForm.advantages} onChange={(e) => setProductForm({ ...productForm, advantages: e.target.value })} />
                <Input placeholder="Applications (comma separated)" value={productForm.applications} onChange={(e) => setProductForm({ ...productForm, applications: e.target.value })} />
                <textarea
                  className="min-h-[96px] rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm outline-none md:col-span-2"
                  placeholder='Specifications JSON: [{"key":"Capacity","value":"500 L"}]'
                  value={productForm.specifications}
                  onChange={(e) => setProductForm({ ...productForm, specifications: e.target.value })}
                />
                <textarea
                  className="min-h-[110px] rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm outline-none md:col-span-2"
                  placeholder="Description"
                  value={productForm.description}
                  onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                  required
                />
                <div className="md:col-span-2 grid gap-2 md:grid-cols-2">
                  <Button type="button" variant="outline" className="border-slate-200 text-slate-700" onClick={cancelProductEditing}>
                    Cancel
                  </Button>
                  <Button type="submit" className="w-full bg-brand-orange text-white hover:bg-orange-600" disabled={loading}>
                    {loading ? "Saving..." : "Update product"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {activeTab === "clients" && (
        <div className="grid gap-6 lg:grid-cols-[1fr_1.1fr]">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-slate-900">Add client</h2>
            <p className="mt-1 text-sm text-slate-500">Create a client profile with logo and business details.</p>

            <form onSubmit={handleClientSubmit} className="mt-6 grid gap-4">
              <Input placeholder="Client name" value={clientForm.name} onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })} required />
              <Input placeholder="Company" value={clientForm.company} onChange={(e) => setClientForm({ ...clientForm, company: e.target.value })} />
              <Input placeholder="Industry" value={clientForm.industry} onChange={(e) => setClientForm({ ...clientForm, industry: e.target.value })} />
              <Input placeholder="Website" value={clientForm.website} onChange={(e) => setClientForm({ ...clientForm, website: e.target.value })} />

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Client Logo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleClientLogoUpload}
                  className="block w-full text-sm text-slate-500 file:mr-4 file:rounded-md file:border-0 file:bg-slate-900 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-slate-700"
                />
                <p className="mt-1 text-xs text-slate-500">Supported: JPEG, PNG, WebP, GIF</p>

                {clientForm.logo && (
                  <div className="relative mt-3 h-20 w-20 overflow-hidden rounded-lg border border-slate-200">
                    <img src={clientForm.logo} alt="Client Logo" className="h-full w-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setClientForm({ ...clientForm, logo: "" })}
                      className="absolute right-0 top-0 rounded-full bg-red-500 p-0.5 text-white hover:bg-red-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-600">
                <input type="checkbox" checked={clientForm.active} onChange={(e) => setClientForm({ ...clientForm, active: e.target.checked })} />
                Active client
              </label>

              <Button type="submit" className="w-full bg-slate-900 text-white hover:bg-slate-700">Save client</Button>
            </form>

            <div className="mt-8 border-t border-slate-100 pt-6">
              <div className="flex items-center gap-2 text-slate-900">
                <Quote className="h-4 w-4 text-brand-orange" />
                <h3 className="text-lg font-semibold">Add testimonial</h3>
              </div>
              <p className="mt-1 text-sm text-slate-500">Showcase customer voices on the homepage with a polished card layout.</p>

              <form onSubmit={handleTestimonialSubmit} className="mt-5 grid gap-4">
                <Input placeholder="Client name" value={testimonialForm.name} onChange={(e) => setTestimonialForm({ ...testimonialForm, name: e.target.value })} required />
                <Input placeholder="Designation" value={testimonialForm.designation} onChange={(e) => setTestimonialForm({ ...testimonialForm, designation: e.target.value })} />
                <textarea
                  className="min-h-[96px] rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm outline-none"
                  placeholder="Testimonial quote"
                  value={testimonialForm.quote}
                  onChange={(e) => setTestimonialForm({ ...testimonialForm, quote: e.target.value })}
                  required
                />
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Rating</label>
                  <select
                    className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm outline-none"
                    value={testimonialForm.rating}
                    onChange={(e) => setTestimonialForm({ ...testimonialForm, rating: Number(e.target.value) })}
                  >
                    {[1, 2, 3, 4, 5].map((value) => <option key={value} value={value}>{value} star{value > 1 ? "s" : ""}</option>)}
                  </select>
                </div>
                <Button type="submit" className="w-full bg-brand-orange text-white hover:bg-orange-600">Save testimonial</Button>
              </form>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Manage clients</h2>
                <p className="mt-1 text-sm text-slate-500">Review and remove client records.</p>
              </div>
              <Button
                type="button"
                variant="outline"
                className="border-slate-200 text-slate-700"
                onClick={async () => {
                  setClientsLoading(true);
                  try {
                    const data = await fetchAdminClients();
                    setClients(Array.isArray(data) ? data : []);
                  } catch {
                    setClients([]);
                  } finally {
                    setClientsLoading(false);
                  }
                }}
              >
                Refresh
              </Button>
            </div>

            <div className="mt-6 space-y-3">
              {clientsLoading ? (
                <p className="text-sm text-slate-500">Loading clients...</p>
              ) : clients.length === 0 ? (
                <p className="text-sm text-slate-500">No clients available.</p>
              ) : (
                clients.map((client) => (
                  <div key={client.id} className="flex flex-col gap-3 rounded-2xl border border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                        {client.logo ? (
                          <img src={client.logo} alt={client.name} className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-xs font-semibold uppercase tracking-wide text-slate-500">
                            {client.name?.slice(0, 2) || "NA"}
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{client.name}</p>
                        <p className="text-sm text-slate-600">{client.company || "No company"}</p>
                        <p className="text-xs text-slate-500">{client.industry || "No industry"}</p>
                      </div>
                    </div>

                    <Button type="button" className="bg-red-600 text-white hover:bg-red-700" onClick={() => promptDeleteClient(client)}>
                      Delete
                    </Button>
                  </div>
                ))
              )}
            </div>

            <div className="mt-8 border-t border-slate-100 pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Manage testimonials</h3>
                  <p className="mt-1 text-sm text-slate-500">Review and remove homepage testimonials.</p>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                {testimonialsLoading ? (
                  <p className="text-sm text-slate-500">Loading testimonials...</p>
                ) : adminTestimonials.length === 0 ? (
                  <p className="text-sm text-slate-500">No testimonials available yet.</p>
                ) : (
                  adminTestimonials.map((testimonial) => (
                    <div key={testimonial.id} className="rounded-2xl border border-slate-200 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-slate-900">{testimonial.name}</p>
                          <p className="text-sm text-slate-600">{testimonial.designation || "Client"}</p>
                        </div>
                        <Button type="button" variant="destructive" className="px-3 py-2" onClick={() => handleDeleteTestimonial(testimonial.id)}>
                          Delete
                        </Button>
                      </div>
                      <p className="mt-3 text-sm text-slate-600">“{testimonial.quote}”</p>
                      <div className="mt-3 flex items-center gap-1 text-brand-orange">
                        {Array.from({ length: testimonial.rating || 0 }).map((_, index) => (
                          <Star key={`${testimonial.id}-${index}`} className="h-4 w-4 fill-current" />
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "inquiries" && (
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 border-b border-slate-200 pb-4">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Inquiries workspace</h2>
              <p className="mt-1 text-sm text-slate-500">Switch between inquiry management and inbox monitoring from one dedicated tab.</p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant={inquiriesSubTab === "manage" ? "primary" : "ghost"}
                className={inquiriesSubTab === "manage" ? "px-4" : "px-4 text-slate-700 hover:bg-slate-100"}
                onClick={() => setInquiriesSubTab("manage")}
              >
                <MessageSquare className="h-4 w-4" />
                <span>Manage inquiries</span>
                <span className={inquiriesSubTab === "manage" ? "rounded-full bg-white/20 px-2 py-0.5 text-xs" : "rounded-full bg-slate-200 px-2 py-0.5 text-xs text-slate-700"}>
                  {inquiries.length}
                </span>
              </Button>

              <Button
                type="button"
                variant={inquiriesSubTab === "inbox" ? "primary" : "ghost"}
                className={inquiriesSubTab === "inbox" ? "px-4" : "px-4 text-slate-700 hover:bg-slate-100"}
                onClick={() => setInquiriesSubTab("inbox")}
              >
                <Inbox className="h-4 w-4" />
                <span>Inbox</span>
                <span className={inquiriesSubTab === "inbox" ? "rounded-full bg-white/20 px-2 py-0.5 text-xs" : "rounded-full bg-slate-200 px-2 py-0.5 text-xs text-slate-700"}>
                  {inboxEmails.length}
                </span>
              </Button>
            </div>
          </div>

          {inquiriesSubTab === "manage" ? (
            <div className="mt-6">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Manage inquiries</h3>
                  <p className="mt-1 text-sm text-slate-500">Track customer requests, update progress and clean up resolved entries.</p>
                </div>

                <div className="flex items-center gap-2">
                  <select
                    value={inquiryStatusFilter}
                    onChange={(event) => setInquiryStatusFilter(event.target.value)}
                    className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm outline-none"
                  >
                    <option value="">All statuses</option>
                    <option value="pending">Pending</option>
                    <option value="in_progress">In progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                  <Button
                    type="button"
                    variant="outline"
                    className="border-slate-200 text-slate-700"
                    onClick={async () => {
                      try {
                        const data = await fetchAdminInquiries(inquiryStatusFilter ? { status: inquiryStatusFilter } : {});
                        setInquiries(Array.isArray(data) ? data : []);
                      } catch {
                        setInquiries([]);
                      }
                    }}
                  >
                    Refresh
                  </Button>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                {inquiriesLoading ? (
                  <p className="text-sm text-slate-500">Loading inquiries...</p>
                ) : inquiries.length === 0 ? (
                  <p className="text-sm text-slate-500">No inquiries found.</p>
                ) : (
                  inquiries.map((inquiry) => (
                    <div key={inquiry.id} className="rounded-2xl border border-slate-200 p-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div className="space-y-1">
                          <p className="font-semibold text-slate-900">{inquiry.name}</p>
                          <p className="text-sm text-slate-600">{inquiry.email} • {inquiry.phone}</p>
                          <p className="text-sm text-slate-600">{inquiry.product_name || "General enquiry"}</p>
                          <p className="text-xs text-slate-500">{formatDateTime(inquiry.created_at)}</p>
                        </div>

                        <div className="flex flex-wrap gap-2 md:justify-end">
                          <select
                            value={inquiry.status || "pending"}
                            onChange={(event) => handleInquiryStatusChange(inquiry, event.target.value)}
                            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm outline-none"
                          >
                            <option value="pending">Pending</option>
                            <option value="in_progress">In progress</option>
                            <option value="resolved">Resolved</option>
                            <option value="closed">Closed</option>
                          </select>
                          <Button type="button" className="bg-red-600 text-white hover:bg-red-700" onClick={() => handleDeleteInquiry(inquiry.id)}>
                            Delete
                          </Button>
                          <Button type="button" variant="outline" className="border-slate-200 text-slate-700" onClick={() => handleComposeEmail(inquiry)}>
                            <Mail className="mr-2 h-4 w-4" />
                            Compose email
                          </Button>
                        </div>
                      </div>

                      <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                        {inquiry.requirements || "No requirements provided."}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="mt-6">
              <div className="flex flex-col gap-4 border-b border-slate-200 pb-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-50 text-brand-orange">
                      <Inbox className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">Sales inbox</h3>
                      <p className="text-xs text-slate-500">Professional mailbox for sales@buildwit.in with Inbox, Outbox and Sent folders.</p>
                    </div>
                  </div>

                  <Button type="button" variant="outline" className="border-slate-200 text-slate-700" onClick={refreshInboxEmails}>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Refresh
                  </Button>
                </div>

                <form onSubmit={handleInboxSearchSubmit} className="grid gap-3 sm:grid-cols-[1fr_auto]">
                  <Input
                    value={inboxSearchInput}
                    onChange={(event) => setInboxSearchInput(event.target.value)}
                    placeholder={mailFolder === "inbox" ? "Search subject, sender or body text" : "Search subject, recipient or message text"}
                  />
                  <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-700">Apply search</Button>
                </form>

                <div className="grid gap-2 sm:grid-cols-3">
                  <button
                    type="button"
                    onClick={() => setMailFolder("inbox")}
                    className={`rounded-xl border px-3 py-2 text-left transition ${mailFolder === "inbox" ? "border-orange-200 bg-orange-50" : "border-slate-200 bg-white hover:border-orange-200 hover:bg-orange-50/50"}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900">
                        <Inbox className="h-4 w-4 text-brand-orange" />
                        Inbox
                      </span>
                      <span className="rounded-full bg-white px-2 py-0.5 text-xs text-slate-700">{inboxEmails.length}</span>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">Incoming customer emails</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setMailFolder("outbox")}
                    className={`rounded-xl border px-3 py-2 text-left transition ${mailFolder === "outbox" ? "border-orange-200 bg-orange-50" : "border-slate-200 bg-white hover:border-orange-200 hover:bg-orange-50/50"}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900">
                        <Clock3 className="h-4 w-4 text-brand-orange" />
                        Outbox
                      </span>
                      <span className="rounded-full bg-white px-2 py-0.5 text-xs text-slate-700">{outboxEmails.length}</span>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">Saved drafts and pending replies</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setMailFolder("sent")}
                    className={`rounded-xl border px-3 py-2 text-left transition ${mailFolder === "sent" ? "border-orange-200 bg-orange-50" : "border-slate-200 bg-white hover:border-orange-200 hover:bg-orange-50/50"}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900">
                        <MailCheck className="h-4 w-4 text-brand-orange" />
                        Sent
                      </span>
                      <span className="rounded-full bg-white px-2 py-0.5 text-xs text-slate-700">{sentEmails.length}</span>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">Delivered outbound communication</p>
                  </button>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
                  <label className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-1.5 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={inboxUnreadOnly}
                      disabled={mailFolder !== "inbox"}
                      onChange={(event) => setInboxUnreadOnly(event.target.checked)}
                    />
                    Show unread only (Inbox)
                  </label>
                  <p>{inboxLastSyncedAt ? `Synced ${formatDateTime(inboxLastSyncedAt)}` : "Waiting for first sync"}</p>
                </div>
              </div>

              {mailboxError ? (
                <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {mailboxError}
                </div>
              ) : null}

              <div className="mt-4 max-h-[620px] space-y-3 overflow-y-auto pr-1">
                {mailboxLoading ? (
                  <p className="text-sm text-slate-500">Loading mailbox emails...</p>
                ) : activeMailboxItems.length === 0 ? (
                  <p className="text-sm text-slate-500">No emails found in this folder for the selected filters.</p>
                ) : (
                  activeMailboxItems.map((mailItem) => (
                    <button
                      key={mailItem.uid || mailItem.id}
                      type="button"
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 p-4 text-left transition hover:border-orange-200 hover:bg-orange-50/60"
                      onClick={() => setSelectedInboxEmail(mailItem)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            {mailFolder === "inbox" && mailItem.unread ? <span className="h-2 w-2 rounded-full bg-brand-orange" /> : <span className="h-2 w-2 rounded-full bg-slate-300" />}
                            <p className="truncate text-sm font-semibold text-slate-900">{mailItem.subject || "(No subject)"}</p>
                          </div>
                          <p className="mt-1 truncate text-sm text-slate-600">
                            {mailFolder === "inbox"
                              ? (mailItem.from_name || mailItem.from_email || "Unknown sender")
                              : (mailItem.to_name || mailItem.to_email || "Unknown recipient")}
                            {mailFolder === "inbox" && mailItem.from_name && mailItem.from_email ? ` <${mailItem.from_email}>` : ""}
                            {mailFolder !== "inbox" && mailItem.to_name && mailItem.to_email ? ` <${mailItem.to_email}>` : ""}
                          </p>
                        </div>

                        <div className="shrink-0 text-right">
                          <p className="text-xs font-medium text-slate-500">{formatEmailDateTime(mailItem.received_at || mailItem.created_at)}</p>
                          {mailItem.has_attachments ? (
                            <span className="mt-1 inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] text-slate-600">
                              <Paperclip className="h-3 w-3" />
                              Attachment
                            </span>
                          ) : null}
                        </div>
                      </div>

                      <p className="mt-3 line-clamp-3 text-sm text-slate-700">{getInboxPreview(mailItem)}</p>

                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {mailFolder === "inbox" ? (
                          <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600">Incoming</span>
                        ) : null}
                        {mailFolder === "outbox" ? (
                          <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">Draft</span>
                        ) : null}
                        {mailFolder === "sent" ? (
                          <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">Sent</span>
                        ) : null}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "inquiries" ? (
        <button
          type="button"
          onClick={openQuickComposeDialog}
          className="fixed bottom-6 right-6 z-40 inline-flex h-12 items-center gap-2 rounded-full bg-slate-900 px-5 text-sm font-semibold text-white shadow-[0_18px_40px_-14px_rgba(15,23,42,0.55)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-slate-700"
        >
          <Mail className="h-4 w-4" />
          Compose email
        </button>
      ) : null}

      {activeTab === "settings" && (
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-xl font-semibold text-slate-900">Change admin password</h2>
          <p className="mt-1 text-sm text-slate-500">Update the password used to sign in to the admin portal.</p>

          <form onSubmit={handlePasswordChange} className="mt-6 grid gap-4 md:max-w-md">
            <Input
              type="password"
              placeholder="Current password"
              value={passwordForm.current_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
              required
            />
            <Input
              type="password"
              placeholder="New password"
              value={passwordForm.new_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
              required
            />
            <Button type="submit" className="w-full bg-slate-900 text-white hover:bg-slate-700">Change password</Button>
          </form>
        </div>
      )}

      <Dialog
        open={composeDialogOpen}
        onOpenChange={(open) => {
          setComposeDialogOpen(open);
          if (!open) {
            setEmailComposer(initialEmailComposer);
          }
        }}
      >
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Compose inquiry email</DialogTitle>
            <DialogDescription>
              Build and send a polished reply directly from the centralized mail system using sales@buildwit.in.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-5">
            <div className="rounded-2xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-900">
              Replying to: <span className="font-semibold">{activeComposeInquiry?.name || "Selected inquiry"}</span>
              <span className="mx-2 text-orange-600">•</span>
              <span>{activeComposeInquiry?.product_name || "General enquiry"}</span>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-slate-700">Quick reply templates</label>
                <div className="grid gap-2 sm:grid-cols-2">
                  <Button type="button" variant={emailComposer.templateKey === "executive_acknowledgement" ? "primary" : "outline"} className="justify-start" onClick={() => handleTemplateChange("executive_acknowledgement")}>Executive acknowledgement</Button>
                  <Button type="button" variant={emailComposer.templateKey === "qualification_request" ? "primary" : "outline"} className="justify-start" onClick={() => handleTemplateChange("qualification_request")}>Qualification request</Button>
                  <Button type="button" variant={emailComposer.templateKey === "proposal_next_steps" ? "primary" : "outline"} className="justify-start" onClick={() => handleTemplateChange("proposal_next_steps")}>Proposal next steps</Button>
                  <Button type="button" variant={emailComposer.templateKey === "stakeholder_meeting" ? "primary" : "outline"} className="justify-start" onClick={() => handleTemplateChange("stakeholder_meeting")}>Stakeholder meeting</Button>
                  <Button type="button" variant={emailComposer.templateKey === "escalation_path" ? "primary" : "outline"} className="justify-start" onClick={() => handleTemplateChange("escalation_path")}>Escalation path</Button>
                  <Button type="button" variant={emailComposer.templateKey === "professional_closure" ? "primary" : "outline"} className="justify-start" onClick={() => handleTemplateChange("professional_closure")}>Professional closure</Button>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">To</label>
                <Input
                  value={emailComposer.to}
                  onChange={(event) => setEmailComposer((prev) => ({ ...prev, to: event.target.value }))}
                  placeholder="customer@company.com"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">CC (optional)</label>
                <Input
                  value={emailComposer.cc}
                  onChange={(event) => setEmailComposer((prev) => ({ ...prev, cc: event.target.value }))}
                  placeholder="team@buildwit.in"
                />
              </div>
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-slate-700">Subject</label>
                <Input
                  value={emailComposer.subject}
                  onChange={(event) => setEmailComposer((prev) => ({ ...prev, subject: event.target.value }))}
                  placeholder="Subject"
                />
              </div>

              <label className="md:col-span-2 flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={emailComposer.includeDetails}
                  onChange={(event) => toggleIncludeDetails(event.target.checked)}
                />
                Include inquiry details in message body
              </label>

              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-slate-700">Attachments</label>
                <div className="flex flex-wrap items-center gap-2">
                  <Button type="button" variant="outline" className="border-slate-200 text-slate-700" onClick={() => fileInputRef.current?.click()}>
                    <Paperclip className="mr-2 h-4 w-4" />
                    Attach files
                  </Button>
                  <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleAttachmentSelection} />
                  <span className="text-sm text-slate-500">{(emailComposer.attachments || []).length ? `${(emailComposer.attachments || []).length} file(s) selected` : "No files attached"}</span>
                </div>
                {(emailComposer.attachments || []).length ? (
                  <ul className="mt-3 space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                    {(emailComposer.attachments || []).map((attachment, index) => (
                      <li key={`${attachment.filename}-${index}`} className="flex items-center justify-between gap-2">
                        <span className="truncate">{attachment.filename}</span>
                        <button type="button" className="text-xs font-medium text-red-600" onClick={() => removeAttachment(index)}>
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-slate-700">Message</label>
                <textarea
                  className="min-h-[220px] w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm outline-none"
                  value={emailComposer.body}
                  onChange={(event) => setEmailComposer((prev) => ({ ...prev, body: event.target.value }))}
                  placeholder="Compose response"
                />
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-2">
              <Button type="button" variant="outline" className="border-slate-200 text-slate-700" onClick={copyEmailDraft}>
                <Copy className="mr-2 h-4 w-4" />
                Copy draft
              </Button>
              <Button type="button" variant="outline" className="border-slate-200 text-slate-700" onClick={saveCurrentEmailAsDraft}>
                Save to outbox
              </Button>
              <Button type="button" className="bg-slate-900 text-white hover:bg-slate-700" onClick={sendEmailReply} disabled={emailSending}>
                <Send className="mr-2 h-4 w-4" />
                {emailSending ? "Sending..." : "Send email"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (open) {
            setDeleteDialogOpen(true);
            return;
          }

          if (!deleteLoading) {
            closeDeleteDialog();
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl">Delete {deleteTarget?.kind === "product" ? "product" : "client"}?</DialogTitle>
            <DialogDescription>
              This action will permanently remove {deleteTarget?.label || "the selected item"} from the admin workspace.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 flex justify-end gap-3">
            <Button type="button" variant="outline" className="border-slate-200 text-slate-700" onClick={closeDeleteDialog} disabled={deleteLoading}>
              Cancel
            </Button>
            <Button type="button" className="bg-red-600 text-white hover:bg-red-700" onClick={confirmDeleteTarget} disabled={deleteLoading || !deleteTarget}>
              {deleteLoading ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(selectedInboxEmail)}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedInboxEmail(null);
          }
        }}
      >
        <DialogContent className="max-h-[92vh] max-w-5xl overflow-hidden p-0">
          <DialogHeader className="border-b border-slate-200 px-6 py-4">
            <DialogTitle className="text-lg">{selectedInboxEmail?.subject || "(No subject)"}</DialogTitle>
            <DialogDescription>
              {mailFolder === "inbox"
                ? (selectedInboxEmail?.from_name || selectedInboxEmail?.from_email || "Unknown sender")
                : (selectedInboxEmail?.to_name || selectedInboxEmail?.to_email || "Unknown recipient")}
              {mailFolder === "inbox" && selectedInboxEmail?.from_name && selectedInboxEmail?.from_email ? ` <${selectedInboxEmail.from_email}>` : ""}
              {mailFolder !== "inbox" && selectedInboxEmail?.to_name && selectedInboxEmail?.to_email ? ` <${selectedInboxEmail.to_email}>` : ""}
              <span className="mx-2">•</span>
              {formatEmailDateTime(selectedInboxEmail?.received_at || selectedInboxEmail?.created_at)}
            </DialogDescription>

            <div className="mt-3 flex flex-wrap gap-2">
              {mailFolder === "inbox" ? (
                <Button type="button" variant="outline" className="border-slate-200 text-slate-700" onClick={() => handleReplyToMailboxEmail(selectedInboxEmail)}>
                  <Reply className="mr-2 h-4 w-4" />
                  Reply now
                </Button>
              ) : null}
              {mailFolder === "outbox" ? (
                <>
                  <Button type="button" variant="outline" className="border-slate-200 text-slate-700" onClick={() => openOutboxDraft(selectedInboxEmail)}>
                    Open draft
                  </Button>
                  <Button type="button" className="bg-red-600 text-white hover:bg-red-700" onClick={() => removeOutboxDraft(selectedInboxEmail?.id)}>
                    Delete draft
                  </Button>
                </>
              ) : null}
            </div>
          </DialogHeader>

          <div className="h-[70vh] w-full bg-white">
            <iframe
              title="Inbox email content"
              sandbox="allow-popups allow-popups-to-escape-sandbox"
              srcDoc={buildInboxEmailDocument(selectedInboxEmail)}
              className="h-full w-full border-0"
            />
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
